import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, Edit3, Eye, FileAudio, ImagePlus, PlusCircle, Trash2, UploadCloud } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { slugify } from '../../utils/slug';
import { routeBuilders } from '../routes';

type Genre = { id: string; name: string; slug?: string };
type Series = {
  id: string;
  title: string;
  description: string | null;
  language: string | null;
  status: string | null;
  slug?: string | null;
  cover_image_url?: string | null;
  series_genres?: { genre_id: string }[];
  cover_image_display?: string | null;
  episodes?: EpisodeWithDisplay[];
};
type Episode = {
  id: string;
  title: string;
  description: string | null;
  episode_number: number | null;
  is_preview: boolean;
  status: string | null;
  audio_file_url: string | null;
  slug?: string | null;
  cover_image_url?: string | null;
};
type EpisodeWithDisplay = Episode & { cover_image_display?: string | null };

const LANG_OPTIONS = ['en', 'es', 'fr', 'de', 'pt', 'it', 'ja', 'ko', 'zh'];
const STATUS_OPTIONS = ['draft', 'published'];
const EPISODES_BUCKET = 'Episodes';
const IMAGES_BUCKET = 'Images';
const DRAFT_STORAGE_PREFIX = 'myseries_draft';

const ensureUniqueSlug = async (table: 'series' | 'episodes', desiredSlug: string, currentId?: string | null) => {
  const base = slugify(desiredSlug || 'item');
  let candidate = base;
  for (let attempt = 0; attempt < 6; attempt++) {
    const query = supabase.from(table).select('id').eq('slug', candidate).limit(1);
    const { data, error } = currentId ? await query.neq('id', currentId) : await query;
    if (error) throw error;
    if (!data || data.length === 0) {
      return candidate;
    }
    candidate = `${base}-${attempt + 2}`;
  }
  return `${base}-${Date.now()}`;
};

const uploadImageToBucket = async (
  file: File,
  pathPrefix: string,
  userId?: string | null,
): Promise<{ path: string; signedUrl: string | null }> => {
  const ext = file.name.split('.').pop() || 'jpg';
  const cleanPrefix = pathPrefix.replace(/\/+/g, '/').replace(/^\/|\/$/g, '');
  const rootedPrefixParts = [];
  if (userId) rootedPrefixParts.push(userId);
  if (cleanPrefix) rootedPrefixParts.push(cleanPrefix);
  const rootedPrefix = rootedPrefixParts.join('/') || 'uploads';
  const fileBase = slugify(file.name.replace(/\.[^/.]+$/, '')) || 'cover';
  const prefixSlug = cleanPrefix ? slugify(cleanPrefix) : 'series';
  const fileName = `${prefixSlug}-${fileBase}-${Date.now()}.${ext}`;
  const path = `${rootedPrefix}/${fileName}`;
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from(IMAGES_BUCKET)
    .upload(path, file, { cacheControl: '3600', upsert: true, contentType: file.type || undefined });
  if (uploadError) throw uploadError;
  const storedPath = uploadData?.path ?? path;
  const { data: signed, error: signError } = await supabase.storage
    .from(IMAGES_BUCKET)
    .createSignedUrl(storedPath, 60 * 60 * 24 * 7);
  if (signError) {
    const { data: pub } = supabase.storage.from(IMAGES_BUCKET).getPublicUrl(storedPath);
    return { path: storedPath, signedUrl: pub?.publicUrl ?? null };
  }
  return { path: storedPath, signedUrl: signed?.signedUrl ?? null };
};

const extractImageStoragePath = (raw?: string | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('http')) {
    return trimmed.replace(/^\/+/, '');
  }
  const bucketSegment = `/${IMAGES_BUCKET}/`;
  const bucketIndex = trimmed.indexOf(bucketSegment);
  if (bucketIndex === -1) return null;
  const withoutParams = trimmed.slice(bucketIndex + bucketSegment.length).split('?')[0];
  return decodeURIComponent(withoutParams);
};

const getDisplayImageUrl = async (raw?: string | null): Promise<string | null> => {
  if (!raw) return null;
  const storagePath = extractImageStoragePath(raw);
  if (!storagePath) return raw;
  const { data, error } = await supabase.storage
    .from(IMAGES_BUCKET)
    .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
  if (!error && data?.signedUrl) {
    return data.signedUrl;
  }
  const { data: publicData } = supabase.storage.from(IMAGES_BUCKET).getPublicUrl(storagePath);
  if (publicData?.publicUrl) {
    return publicData.publicUrl;
  }
  if (error) {
    console.error('Unable to generate signed URL for image', error);
  }
  return raw;
};

interface MySeriesProps {
  userId: string;
  genres: Genre[];
  initialSeriesSlug?: string | null;
  onRouteChange?: (seriesSlug: string | null, options?: { startNewSeries?: boolean }) => void;
}

export function MySeries({ userId, genres, initialSeriesSlug = null, onRouteChange }: MySeriesProps) {
  const location = useLocation();
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const [seriesForm, setSeriesForm] = useState({
    id: '',
    title: '',
    slug: '',
    description: '',
    language: 'en',
    status: 'draft',
    genreIds: [] as string[],
    coverImageUrl: '',
    coverFile: null as File | null,
  });
  const [seriesSlugEdited, setSeriesSlugEdited] = useState(false);
  const [seriesCoverPreview, setSeriesCoverPreview] = useState<string | null>(null);
  const [episodeForm, setEpisodeForm] = useState({
    id: '',
    title: '',
    slug: '',
    description: '',
    episodeNumber: 1,
    isPreview: false,
    status: 'draft',
    audioFile: null as File | null,
    audioUrl: '',
    coverUrl: '',
  });
  const [episodeSlugEdited, setEpisodeSlugEdited] = useState(false);
  const [seriesCoverDisplay, setSeriesCoverDisplay] = useState<string | null>(null);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [savingSeries, setSavingSeries] = useState(false);
  const [savingEpisode, setSavingEpisode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [initialSeriesHandled, setInitialSeriesHandled] = useState(false);
  const previousSeriesIdRef = useRef<string | null>(null);
  const seriesCoverInputRef = useRef<HTMLInputElement | null>(null);
  const seriesCoverFileRef = useRef<File | null>(null);
  const draftKey = useMemo(() => (userId ? `${DRAFT_STORAGE_PREFIX}_${userId}` : null), [userId]);
  const creationDraftKey = useMemo(
    () => (userId ? `${DRAFT_STORAGE_PREFIX}_creation_${userId}` : null),
    [userId],
  );
  const [creationStep, setCreationStep] = useState<1 | 2 | 3 | 4 | null>(null);
  const [creationSeriesId, setCreationSeriesId] = useState<string | null>(null);
  const [creationSlug, setCreationSlug] = useState<string | null>(null);
  const [primaryGenreId, setPrimaryGenreId] = useState<string>('');
  const [secondaryGenreIds, setSecondaryGenreIds] = useState<string[]>([]);
  const [audienceRating, setAudienceRating] = useState<string>('Everyone');
  const [isExplicit, setIsExplicit] = useState(false);
  const [releaseSchedule, setReleaseSchedule] = useState('');

  const selectedSeries = useMemo(
    () => seriesList.find((s) => s.id === selectedSeriesId) ?? null,
    [seriesList, selectedSeriesId],
  );

  const resetSeriesForm = useCallback(() => {
    setSeriesForm({
      id: '',
      title: '',
      slug: '',
      description: '',
      language: 'en',
      status: 'draft',
      genreIds: [],
      coverImageUrl: '',
      coverFile: null,
    });
    setSeriesSlugEdited(false);
    setSeriesCoverDisplay(null);
    if (seriesCoverPreview) {
      URL.revokeObjectURL(seriesCoverPreview);
      setSeriesCoverPreview(null);
    }
  }, [seriesCoverPreview]);

  const resetEpisodeForm = useCallback(() => {
    setEpisodeForm({
      id: '',
      title: '',
      slug: '',
      description: '',
      episodeNumber: 1,
      isPreview: false,
      status: 'draft',
      audioFile: null,
      audioUrl: '',
      coverUrl: '',
    });
    setEpisodeSlugEdited(false);
  }, []);

  const startNewSeriesLocal = useCallback(() => {
    setSelectedSeriesId(null);
    resetSeriesForm();
    resetEpisodeForm();
    setCreationStep(1);
    setCreationSeriesId(null);
    setCreationSlug(null);
    setPrimaryGenreId('');
    setSecondaryGenreIds([]);
    setAudienceRating('Everyone');
    setIsExplicit(false);
    setReleaseSchedule('');
    setView('detail');
    setError(null);
    setSuccess(null);
  }, [resetEpisodeForm, resetSeriesForm]);

  useEffect(() => {
    return () => {
      if (seriesCoverPreview?.startsWith('blob:')) URL.revokeObjectURL(seriesCoverPreview);
    };
  }, [seriesCoverPreview]);

  useEffect(() => {
    if (!creationDraftKey) return;
    if (selectedSeriesId || initialSeriesSlug) return;
    const rawDraft = localStorage.getItem(creationDraftKey);
    if (!rawDraft) return;
    try {
      const parsed = JSON.parse(rawDraft) as {
        creationStep?: 1 | 2 | 3 | 4;
        creationSeriesId?: string | null;
        creationSlug?: string | null;
        primaryGenreId?: string;
        secondaryGenreIds?: string[];
        audienceRating?: string;
        isExplicit?: boolean;
        releaseSchedule?: string;
        seriesForm?: Partial<typeof seriesForm>;
      };
      if (!parsed.creationStep) return;
      if (creationStep && creationStep >= parsed.creationStep) return;
      setCreationStep(parsed.creationStep);
      setCreationSeriesId(parsed.creationSeriesId ?? null);
      setCreationSlug(parsed.creationSlug ?? null);
      setPrimaryGenreId(parsed.primaryGenreId ?? '');
      setSecondaryGenreIds(parsed.secondaryGenreIds ?? []);
      setAudienceRating(parsed.audienceRating ?? 'Everyone');
      setIsExplicit(parsed.isExplicit ?? false);
      setReleaseSchedule(parsed.releaseSchedule ?? '');
      if (parsed.seriesForm) {
        setSeriesForm((prev) => ({ ...prev, ...parsed.seriesForm, coverFile: null }));
      }
      if (parsed.creationSeriesId) {
        setSelectedSeriesId(parsed.creationSeriesId);
      } else {
        setSelectedSeriesId(null);
      }
      setView('detail');
      setError(null);
      setSuccess(null);
    } catch (err) {
      console.error('Unable to restore creation draft', err);
    }
  }, [creationDraftKey, creationStep, initialSeriesSlug, selectedSeriesId, seriesForm]);

  useEffect(() => {
    let isMounted = true;
    if (seriesCoverPreview) {
      setSeriesCoverDisplay(seriesCoverPreview);
      return;
    }
    (async () => {
      const displayUrl = await getDisplayImageUrl(seriesForm.coverImageUrl);
      if (isMounted) {
        setSeriesCoverDisplay(displayUrl);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [seriesCoverPreview, seriesForm.coverImageUrl]);

  useEffect(() => {
    setEpisodeForm((prev) => ({ ...prev, coverUrl: seriesCoverDisplay || seriesForm.coverImageUrl }));
  }, [seriesCoverDisplay, seriesForm.coverImageUrl]);

  const loadSeries = useCallback(async () => {
    setLoadingSeries(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('series')
      .select(
        `
        id,
        title,
        description,
        language,
        status,
        slug,
        cover_image_url,
        series_genres ( genre_id ),
        episodes (
          id,
          title,
          description,
          episode_number,
          is_preview,
          status,
          audio_file_url,
          slug,
          cover_image_url
        )
      `,
      )
      .eq('creator_id', userId)
      .order('created_at', { ascending: false })
      .order('episode_number', { ascending: true, foreignTable: 'episodes' });

    if (fetchError) {
      setError(fetchError.message);
      setLoadingSeries(false);
      return;
    }

    const withDisplayUrls =
      (await Promise.all(
        (data ?? []).map(async (s) => ({
          ...s,
          cover_image_display: await getDisplayImageUrl(s.cover_image_url),
          episodes: await Promise.all(
            (s.episodes ?? []).map(async (ep) => ({
              ...ep,
              cover_image_display: await getDisplayImageUrl(ep.cover_image_url),
            })),
          ),
        })),
      )) ?? [];

    setSeriesList(withDisplayUrls);
    setLoadingSeries(false);
  }, [getDisplayImageUrl, userId]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries, userId]);

  useEffect(() => {
    setInitialSeriesHandled(false);
  }, [initialSeriesSlug]);

  useEffect(() => {
    if (!initialSeriesSlug) return;
    if (initialSeriesHandled) return;
    if (!seriesList.length) return;

    const match = seriesList.find((s) => (s.slug || slugify(s.title)) === initialSeriesSlug);
    if (match) {
      setCreationStep(null);
      setCreationSeriesId(null);
      setCreationSlug(null);
      setSelectedSeriesId(match.id);
      setView('detail');
    } else {
      setError('Series not found or not available.');
      onRouteChange?.(null);
      setView('list');
    }
    setInitialSeriesHandled(true);
  }, [initialSeriesSlug, initialSeriesHandled, onRouteChange, seriesList]);

  // Restore draft for new series/episode if available
  useEffect(() => {
    if (!draftKey) return;
    if (selectedSeriesId || initialSeriesSlug) return;
    const rawDraft = localStorage.getItem(draftKey);
    if (!rawDraft) return;
    try {
      const parsed = JSON.parse(rawDraft) as {
        seriesForm?: Partial<typeof seriesForm>;
        episodeForm?: Partial<typeof episodeForm>;
        view?: 'list' | 'detail';
      };
      if (parsed.seriesForm) {
        setSeriesForm((prev) => ({ ...prev, ...parsed.seriesForm, coverFile: null }));
      }
      if (parsed.episodeForm) {
        setEpisodeForm((prev) => ({ ...prev, ...parsed.episodeForm, audioFile: null }));
      }
      if (parsed.view) {
        setView(parsed.view);
      }
    } catch (err) {
      console.error('Unable to load draft', err);
    }
  }, [draftKey, initialSeriesSlug, selectedSeriesId]);

  const persistDraft = useCallback(() => {
    if (!draftKey) return;
    if (selectedSeriesId || initialSeriesSlug) return;
    if (view !== 'detail') return;
    const { coverFile: _cf, ...seriesData } = seriesForm;
    const { audioFile: _epAf, ...episodeData } = episodeForm;
    const payload = {
      seriesForm: seriesData,
      episodeForm: episodeData,
      view,
    };
    try {
      localStorage.setItem(draftKey, JSON.stringify(payload));
    } catch (err) {
      console.error('Unable to persist draft', err);
    }
  }, [draftKey, episodeForm, initialSeriesSlug, selectedSeriesId, seriesForm, view]);

  const clearDraft = useCallback(() => {
    if (draftKey) localStorage.removeItem(draftKey);
    if (creationDraftKey) localStorage.removeItem(creationDraftKey);
  }, [creationDraftKey, draftKey]);

  useEffect(() => {
    persistDraft();
  }, [episodeForm, persistDraft, seriesForm, view]);

  const handleSeriesCoverSelection = useCallback(
    (file: File | null) => {
      setCreationStep((prev) => prev || 3);
      setView('detail');
      setError(null);
      setSeriesForm((prev) => ({ ...prev, coverFile: file }));
      seriesCoverFileRef.current = file;
      if (seriesCoverPreview?.startsWith('blob:')) URL.revokeObjectURL(seriesCoverPreview);
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            setSeriesCoverPreview(reader.result);
            setSeriesCoverDisplay(reader.result);
          }
        };
        reader.readAsDataURL(file);
        setSuccess('Cover ready — save to upload');
      } else {
        setSeriesCoverPreview(null);
        setSeriesCoverDisplay(null);
        setSuccess(null);
      }
    },
    [seriesCoverPreview],
  );

  const persistCreationDraft = useCallback(() => {
    if (!creationDraftKey) return;
    if (!creationStep) {
      localStorage.removeItem(creationDraftKey);
      return;
    }
    let existingStep = 0;
    try {
      const existingRaw = localStorage.getItem(creationDraftKey);
      if (existingRaw) {
        const parsed = JSON.parse(existingRaw) as { creationStep?: number };
        existingStep = Number(parsed?.creationStep) || 0;
      }
    } catch {
      // ignore parse errors and overwrite below
    }
    if (existingStep > (creationStep ?? 0)) {
      return; // never downgrade a saved wizard step
    }
    const { coverFile: _cf, ...seriesData } = seriesForm;
    const payload = {
      creationStep,
      creationSeriesId,
      creationSlug,
      primaryGenreId,
      secondaryGenreIds,
      audienceRating,
      isExplicit,
      releaseSchedule,
      seriesForm: seriesData,
    };
    try {
      localStorage.setItem(creationDraftKey, JSON.stringify(payload));
    } catch (err) {
      console.error('Unable to persist creation draft', err);
    }
  }, [
    audienceRating,
    creationDraftKey,
    creationSeriesId,
    creationSlug,
    creationStep,
    isExplicit,
    primaryGenreId,
    releaseSchedule,
    secondaryGenreIds,
    seriesForm,
  ]);

  useEffect(() => {
    persistCreationDraft();
  }, [persistCreationDraft]);

  useEffect(() => {
    if (selectedSeriesId) {
      if (selectedSeries) {
        setSeriesForm({
          id: selectedSeries.id,
          title: selectedSeries.title,
          slug: selectedSeries.slug ?? slugify(selectedSeries.title),
          description: selectedSeries.description ?? '',
          language: selectedSeries.language ?? 'en',
          status: selectedSeries.status ?? 'draft',
          genreIds: (selectedSeries.series_genres ?? []).map((g) => g.genre_id),
          coverImageUrl: selectedSeries.cover_image_url ?? '',
          coverFile: null,
        });
        setSeriesSlugEdited(Boolean(selectedSeries.slug));
        resetEpisodeForm();
        previousSeriesIdRef.current = selectedSeriesId;
      }
      return;
    }

    if (previousSeriesIdRef.current !== null) {
      resetSeriesForm();
      resetEpisodeForm();
      previousSeriesIdRef.current = null;
    }
  }, [resetEpisodeForm, resetSeriesForm, selectedSeries, selectedSeriesId]);

  const handleStartNewSeries = () => {
    if (creationDraftKey) {
      localStorage.removeItem(creationDraftKey);
    }
    startNewSeriesLocal();
    seriesCoverFileRef.current = null;
    onRouteChange?.(null, { startNewSeries: true });
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedSeriesId(null);
    setCreationStep(null);
    setCreationSeriesId(null);
    setCreationSlug(null);
    setPrimaryGenreId('');
    setSecondaryGenreIds([]);
    setAudienceRating('Everyone');
    setIsExplicit(false);
    setReleaseSchedule('');
    seriesCoverFileRef.current = null;
    resetSeriesForm();
    resetEpisodeForm();
    setError(null);
    setSuccess(null);
    clearDraft();
    onRouteChange?.(null);
  };

  useEffect(() => {
    const startNew = (location.state as { startNewSeries?: boolean } | null)?.startNewSeries;
    if (!startNew) return;
    startNewSeriesLocal();
  }, [location.state, startNewSeriesLocal]);

  const handleSeriesSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSeries(true);
    setError(null);
    setSuccess(null);

    try {
      const uniqueSlug = await ensureUniqueSlug('series', seriesForm.slug || seriesForm.title, seriesForm.id || null);
      const coverFile = seriesCoverInputRef.current?.files?.[0] ?? seriesForm.coverFile;
      let coverUrl = seriesForm.coverImageUrl;
      let coverSignedUrl: string | null = null;
      if (coverFile) {
        const uploaded = await uploadImageToBucket(coverFile, `series/${uniqueSlug}`, userId);
        coverUrl = uploaded.path;
        coverSignedUrl = uploaded.signedUrl;
        if (!coverUrl) {
          throw new Error('Cover upload failed. Please try again.');
        }
      }

      let newSeriesId = seriesForm.id;
      const payload = {
        title: seriesForm.title,
        description: seriesForm.description,
        language: seriesForm.language,
        status: seriesForm.status,
        slug: uniqueSlug,
        cover_image_url: coverUrl,
      };

      if (seriesForm.id) {
        const { error: updateError } = await supabase.from('series').update(payload).eq('id', seriesForm.id);
        if (updateError) throw updateError;
      } else {
        const { data, error: insertError } = await supabase
          .from('series')
          .insert({ ...payload, creator_id: userId })
          .select('id')
          .single();
        if (insertError) throw insertError;
        newSeriesId = data?.id;
      }

      const targetSeriesId = seriesForm.id || newSeriesId;
      if (!targetSeriesId) throw new Error('Series id missing after save.');

      await supabase.from('series_genres').delete().eq('series_id', targetSeriesId);
      if (seriesForm.genreIds.length) {
        const { error: genreError } = await supabase.from('series_genres').insert(
          seriesForm.genreIds.map((genreId) => ({
            series_id: targetSeriesId,
            genre_id: genreId,
          })),
        );
        if (genreError) throw genreError;
      }

      setSuccess(seriesForm.id ? 'Series updated' : 'Series created');
      clearDraft();
      await loadSeries();
      if (!seriesForm.id && newSeriesId) {
        setSelectedSeriesId(newSeriesId);
      }
      setSeriesForm((prev) => ({
        ...prev,
        id: targetSeriesId,
        slug: uniqueSlug,
        coverImageUrl: coverUrl,
        coverFile: null,
      }));
      if (seriesCoverInputRef.current) {
        seriesCoverInputRef.current.value = '';
      }
      setSeriesCoverPreview(coverSignedUrl);
      setSeriesCoverDisplay(coverSignedUrl ?? (await getDisplayImageUrl(coverUrl)));
      setSeriesSlugEdited(true);
      setView('detail');
      onRouteChange?.(uniqueSlug);
    } catch (err: any) {
      setError(err.message ?? 'Unable to save series.');
    } finally {
      setSavingSeries(false);
    }
  };

  const handleCreateStep1 = async () => {
    if (!userId) {
      setError('You must be signed in to create a series.');
      return;
    }
    if (!seriesForm.title.trim() || !seriesForm.description.trim() || !primaryGenreId || !seriesForm.language) {
      setError('Please fill in title, description, language, and primary genre.');
      return;
    }
    setSavingSeries(true);
    setError(null);
    setSuccess(null);
    try {
      const uniqueSlug = await ensureUniqueSlug('series', seriesForm.slug || seriesForm.title, null);
      const { data, error: insertError } = await supabase
        .from('series')
        .insert({
          title: seriesForm.title,
          description: seriesForm.description,
          language: seriesForm.language,
          status: 'draft',
          slug: uniqueSlug,
          creator_id: userId,
        })
        .select('id')
        .single();
      if (insertError) throw insertError;
      const newId = data?.id as string | null;
      if (!newId) throw new Error('Series id missing after save.');
      await supabase.from('series_genres').delete().eq('series_id', newId);
      await supabase
        .from('series_genres')
        .insert({ series_id: newId, genre_id: primaryGenreId, primary_genre: true });
      setCreationSeriesId(newId);
      setCreationSlug(uniqueSlug);
      setSelectedSeriesId(newId);
      setSeriesForm((prev) => ({ ...prev, id: newId, slug: uniqueSlug, status: 'draft' }));
      setSuccess('saved as draft succesfuly');
      setCreationStep(2);
    } catch (err: any) {
      setError(err.message ?? 'Unable to start series draft.');
    } finally {
      setSavingSeries(false);
    }
  };

  const persistMetadata = useCallback(
    async (seriesId: string) => {
      const { error } = await supabase
        .from('series')
        .update({
          audience_rating: audienceRating || null,
          is_explicit: isExplicit,
          release_schedule: releaseSchedule || null,
        })
        .eq('id', seriesId);
      if (error) throw error;

      if (primaryGenreId || secondaryGenreIds.length) {
        await supabase.from('series_genres').delete().eq('series_id', seriesId);
        const inserts = [
          ...(primaryGenreId ? [{ series_id: seriesId, genre_id: primaryGenreId, primary_genre: true }] : []),
          ...secondaryGenreIds.slice(0, 3).map((genreId) => ({
            series_id: seriesId,
            genre_id: genreId,
            primary_genre: false,
          })),
        ];
        if (inserts.length) {
          const { error: genreError } = await supabase.from('series_genres').insert(inserts);
          if (genreError) throw genreError;
        }
      }
    },
    [audienceRating, isExplicit, primaryGenreId, releaseSchedule, secondaryGenreIds],
  );

  const handleCreateStep2Continue = async () => {
    if (!creationSeriesId) {
      setError('Start with Step 1 before continuing.');
      return;
    }
    setSavingSeries(true);
    setError(null);
    setSuccess(null);
    try {
      await persistMetadata(creationSeriesId);
      setSuccess('saved as draft succesfuly');
      setCreationStep(3);
    } catch (err: any) {
      setError(err.message ?? 'Unable to save metadata.');
    } finally {
      setSavingSeries(false);
    }
  };

  const handleCreateStep2Skip = () => {
    if (!creationSeriesId) {
      setError('Start with Step 1 before continuing.');
      return;
    }
    setCreationStep(3);
    setError(null);
    setSuccess(null);
  };

  const handleCreateStep3 = async () => {
    if (!creationSeriesId) {
      setError('Start with Step 1 before continuing.');
      return;
    }
    const coverFile = seriesForm.coverFile ?? seriesCoverFileRef.current ?? seriesCoverInputRef.current?.files?.[0] ?? null;
    if (!coverFile && !seriesForm.coverImageUrl) {
      setError('Please upload a cover image.');
      return;
    }
    setCreationStep((prev) => prev || 3);
    setView('detail');
    setSavingSeries(true);
    setError(null);
    setSuccess(null);
    try {
      let coverUrl = seriesForm.coverImageUrl;
      if (coverFile) {
        const uploaded = await uploadImageToBucket(
          coverFile,
          `series/${creationSlug || seriesForm.slug || 'series'}`,
          userId,
        );
        coverUrl = uploaded.path;
        const displayUrl = uploaded.signedUrl ?? (await getDisplayImageUrl(uploaded.path));
        setSeriesCoverPreview(displayUrl);
        setSeriesCoverDisplay(displayUrl ?? uploaded.path);
      }

      const { error } = await supabase
        .from('series')
        .update({ cover_image_url: coverUrl })
        .eq('id', creationSeriesId);
      if (error) throw error;
      setSeriesForm((prev) => ({ ...prev, coverImageUrl: coverUrl || prev.coverImageUrl }));
      setSuccess('saved as draft succesfuly');
      setCreationStep(4);
    } catch (err: any) {
      setError(err.message ?? 'Unable to save cover image.');
    } finally {
      setSavingSeries(false);
    }
  };

  const handleSeriesDelete = async (id: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this series and all its episodes?');
    if (!confirmed) return;
    setError(null);
    setSuccess(null);
    try {
      await supabase.from('episodes').delete().eq('series_id', id);
      await supabase.from('series_genres').delete().eq('series_id', id);
      const { error: delError } = await supabase.from('series').delete().eq('id', id);
      if (delError) throw delError;
      if (selectedSeriesId === id) {
        setSelectedSeriesId(null);
        setView('list');
        resetSeriesForm();
        resetEpisodeForm();
        clearDraft();
        onRouteChange?.(null);
      }
      await loadSeries();
      setSuccess('Series deleted');
    } catch (err: any) {
      setError(err.message ?? 'Unable to delete series.');
    }
  };

  const handleEpisodeSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedSeriesId) {
      setError('Save the series first, then add episodes.');
      return;
    }
    if (!episodeForm.id && !episodeForm.audioFile && !episodeForm.audioUrl) {
      setError('Please upload an audio file for this episode.');
      return;
    }
    setSavingEpisode(true);
    setError(null);
    setSuccess(null);

    try {
      const uniqueSlug = await ensureUniqueSlug('episodes', episodeForm.slug || episodeForm.title, episodeForm.id || null);

      let audioUrl = episodeForm.audioUrl;
      if (episodeForm.audioFile) {
        const ext = episodeForm.audioFile.name.split('.').pop() || 'mp3';
        const fileName = `${uniqueSlug}-${Date.now()}.${ext}`;
        const path = `${userId}/${selectedSeriesId}/${fileName}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(EPISODES_BUCKET)
          .upload(path, episodeForm.audioFile, { cacheControl: '3600', upsert: true });
        if (uploadError) throw uploadError;
        audioUrl = uploadData?.path ?? path;
      }

      const coverUrl = seriesCoverPreview || seriesCoverDisplay || seriesForm.coverImageUrl || selectedSeries?.cover_image_url || '';

      const payload = {
        title: episodeForm.title,
        description: episodeForm.description,
        episode_number: episodeForm.episodeNumber,
        is_preview: episodeForm.isPreview,
        status: episodeForm.status,
        audio_file_url: audioUrl,
        series_id: selectedSeriesId,
        slug: uniqueSlug,
        cover_image_url: coverUrl,
      };

      if (episodeForm.id) {
        const { error: updateError } = await supabase.from('episodes').update(payload).eq('id', episodeForm.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase.from('episodes').insert(payload);
        if (insertError) throw insertError;
      }

    setSuccess(episodeForm.id ? 'Episode updated' : 'Episode created');
    await loadSeries();
  } catch (err: any) {
      setError(err.message ?? 'Unable to save episode.');
  } finally {
      setSavingEpisode(false);
      resetEpisodeForm();
      setEpisodeForm((prev) => ({ ...prev, coverUrl }));
    }
  };

  const handleEpisodeEdit = (ep: Episode) => {
    resetEpisodeForm();
    setEpisodeForm({
      id: ep.id,
      title: ep.title,
      slug: ep.slug ?? slugify(ep.title),
      description: ep.description ?? '',
      episodeNumber: ep.episode_number ?? 1,
      isPreview: ep.is_preview,
      status: ep.status ?? 'draft',
      audioFile: null,
      audioUrl: ep.audio_file_url ?? '',
      coverUrl: seriesCoverDisplay || seriesForm.coverImageUrl || selectedSeries?.cover_image_url || '',
    });
    setEpisodeSlugEdited(Boolean(ep.slug));
  };

  const handleEpisodeDelete = async (id: string) => {
    if (!window.confirm('Delete this episode?')) return;
    setError(null);
    setSuccess(null);
    const { error: delError } = await supabase.from('episodes').delete().eq('id', id);
    if (delError) {
      setError(delError.message);
      return;
    }
    await loadSeries();
    setSuccess('Episode deleted');
  };

  const renderAlerts = () => {
    if (!error && !success) return null;
    return (
      <div className="rounded-2xl border border-base-200 p-4 bg-base-200 text-sm shadow-md">
        {error && <p className="text-error">{error}</p>}
        {success && <p className="text-success">{success}</p>}
      </div>
    );
  };

  const renderListView = () => (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">My series</h1>
          <p className="text-base-content/60">Browse all your series at a glance.</p>
        </div>
        <button
          onClick={handleStartNewSeries}
          className="btn btn-primary"
        >
          <PlusCircle className="w-5 h-5" />
          New series
        </button>
      </div>

      {renderAlerts()}

      <div className="rounded-md border border-base-200 overflow-hidden shadow-md">
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-200">
          <h2 className="text-lg font-semibold">All series</h2>
          {loadingSeries && <span className="text-sm text-base-content/60">Loading…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="table w-full text-sm">
            <thead className="bg-base-200 text-base-content/70">
              <tr>
                <th className="text-left font-medium px-4 py-3">Title</th>
                <th className="text-left font-medium px-4 py-3">Episodes</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {seriesList.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-base-content/60">
                    No series yet. Create your first one.
                  </td>
                </tr>
              )}
              {seriesList.map((s) => {
                const seriesSlug = s.slug ?? slugify(s.title);
                const isPublished = (s.status ?? 'draft') === 'published';
                return (
                  <tr key={s.id} className="border-t border-base-200 hover:bg-base-200">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-base-200 overflow-hidden flex-shrink-0">
                          {s.cover_image_display || s.cover_image_url ? (
                            <img
                              src={s.cover_image_display || s.cover_image_url || ''}
                              alt={s.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-base-content/50">
                              <ImagePlus className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{s.title}</p>
                          <p className="text-xs text-base-content/60 line-clamp-1">{s.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-base-content/80">{(s.episodes ?? []).length}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          isPublished
                            ? 'bg-success/10 text-success border border-success/30'
                            : 'bg-warning/10 text-warning border border-warning/30'
                        }`}
                      >
                        {s.status ?? 'draft'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {isPublished && (
                          <button
                            onClick={() => window.open(routeBuilders.seriesPublic(seriesSlug), '_blank')}
                            className="btn btn-ghost btn-primary btn-square btn-sm"
                            aria-label="View series landing page"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setCreationStep(null);
                            setCreationSeriesId(null);
                            setCreationSlug(null);
                            setPrimaryGenreId('');
                            setSecondaryGenreIds([]);
                            setAudienceRating('Everyone');
                            setIsExplicit(false);
                            setReleaseSchedule('');
                            setSelectedSeriesId(s.id);
                            setView('detail');
                            setError(null);
                            setSuccess(null);
                            onRouteChange?.(seriesSlug);
                          }}
                          className="btn btn-ghost btn-primary btn-square btn-sm"
                          aria-label="Edit series"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSeriesDelete(s.id)}
                          className="btn btn-ghost btn-error btn-square btn-sm"
                          aria-label="Delete series"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const seriesCover = seriesCoverPreview || seriesCoverDisplay || seriesForm.coverImageUrl;
  const creationStepsConfig = [
    { id: 1 as const, title: 'Main details', description: 'Title, short description, language, primary genre' },
    { id: 2 as const, title: 'Discovery & metadata', description: 'Tags, audience rating, content warnings, schedule' },
    { id: 3 as const, title: 'Series art & presentation', description: 'Upload cover art' },
    { id: 4 as const, title: 'Confirmation', description: 'Review and continue' },
  ];

  const renderCreationStepper = () => {
    if (!creationStep) return null;
    return (
      <div className="grid md:grid-cols-4 gap-3">
        {creationStepsConfig.map((step) => {
          const isActive = creationStep === step.id;
          const isComplete = (creationStep ?? 1) > step.id;
          return (
            <div
              key={step.id}
              className={`rounded-2xl border px-4 py-3 transition-colors ${
                isActive
                  ? 'border-primary bg-primary text-primary-content shadow-lg'
                  : isComplete
                  ? 'border-success/30 bg-success/10 text-success'
                  : 'border-base-200 bg-base-100 text-base-content'
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center border ${
                    isActive
                      ? 'bg-primary-content text-primary border-primary-content'
                      : isComplete
                      ? 'bg-success text-success-content border-success'
                      : 'bg-base-200 text-base-content/80 border-base-200'
                  }`}
                >
                  {step.id}
                </span>
                {step.title}
              </div>
              <p className={`text-xs mt-1 ${isActive ? 'text-primary-content/80' : 'text-base-content/70'}`}>{step.description}</p>
            </div>
          );
        })}
      </div>
    );
  };

  const handleFinishToEpisodes = async () => {
    if (creationSeriesId) {
      await loadSeries();
      setSelectedSeriesId(creationSeriesId);
      setCreationStep(null);
      setCreationSeriesId(null);
      setCreationSlug(null);
      seriesCoverFileRef.current = null;
      setView('detail');
      setError(null);
      onRouteChange?.(creationSlug ?? null);
      if (creationDraftKey) localStorage.removeItem(creationDraftKey);
    } else {
      setCreationStep(null);
      setView('detail');
    }
  };

  const handleFinishToDashboard = async () => {
    await loadSeries();
    handleBackToList();
    if (creationDraftKey) localStorage.removeItem(creationDraftKey);
    seriesCoverFileRef.current = null;
  };

  const renderCreationWizard = () => {
    if (!creationStep) return null;
    const primaryGenreName = genres.find((g) => g.id === primaryGenreId)?.name;
    const secondaryNames = secondaryGenreIds
      .map((id) => genres.find((g) => g.id === id)?.name)
      .filter(Boolean)
      .join(', ');

    const renderStep1 = () => (
      <div className="rounded-2xl border border-base-200 bg-base-100 p-6 shadow-md space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-base-content/70 block mb-1">Series title</label>
            <input
              required
              value={seriesForm.title}
              onChange={(e) => {
                const nextTitle = e.target.value;
                setSeriesForm((prev) => ({
                  ...prev,
                  title: nextTitle,
                  slug: seriesSlugEdited ? prev.slug : slugify(nextTitle),
                }));
              }}
              className="input input-bordered w-full"
              placeholder="e.g. Science Weekly"
            />
          </div>
          <div>
            <label className="text-sm text-base-content/70 block mb-1">Language</label>
            <select
              value={seriesForm.language}
              onChange={(e) => setSeriesForm((prev) => ({ ...prev, language: e.target.value }))}
              className="select select-bordered w-full"
            >
              {LANG_OPTIONS.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm text-base-content/70 block mb-1">Short description</label>
          <textarea
            required
            value={seriesForm.description}
            onChange={(e) => setSeriesForm((prev) => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="textarea textarea-bordered w-full"
            placeholder="1–2 sentences that will appear in listings"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-base-content/70 block mb-1">Primary genre</label>
            <select
              value={primaryGenreId}
              onChange={(e) => setPrimaryGenreId(e.target.value)}
              className="select select-bordered w-full"
            >
              <option value="">Select a primary genre</option>
              {genres.map((genre) => (
                <option key={genre.id} value={genre.id}>
                  {genre.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleCreateStep1}
            disabled={savingSeries}
            className="btn btn-primary"
          >
            {savingSeries ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    );

    const renderStep2 = () => (
      <div className="rounded-2xl border border-base-200 bg-base-100 p-6 shadow-md space-y-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-base-content/70">Secondary genres / tags (max 3)</label>
            <span className="text-xs text-base-content/60">{secondaryGenreIds.length}/3</span>
          </div>
          <div className="grid md:grid-cols-3 gap-2 max-h-48 overflow-y-auto rounded-2xl border border-base-200 p-3">
            {genres.map((genre) => {
              const disabled = primaryGenreId === genre.id && !secondaryGenreIds.includes(genre.id);
              const checked = secondaryGenreIds.includes(genre.id);
              return (
                <label key={genre.id} className="flex items-center gap-2 text-sm text-base-content/80">
                  <input
                    type="checkbox"
                    disabled={disabled || (!checked && secondaryGenreIds.length >= 3)}
                    checked={checked}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setSecondaryGenreIds((prev) => {
                        if (isChecked) {
                          if (prev.includes(genre.id) || genre.id === primaryGenreId) return prev;
                          if (prev.length >= 3) return prev;
                          return [...prev, genre.id];
                        }
                        return prev.filter((id) => id !== genre.id);
                      });
                    }}
                    className="checkbox checkbox-sm"
                  />
                  <span>{genre.name}</span>
                  {disabled && <span className="text-[10px] text-base-content/50">(primary)</span>}
                </label>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm text-base-content/70 block">Audience rating</label>
            <div className="flex flex-wrap gap-2">
              {['Everyone', '13+', '18+'].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setAudienceRating(option)}
                  className={`btn btn-sm ${audienceRating === option ? 'btn-primary' : 'btn-outline btn-primary'}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-base-content/70 block">Content warnings</label>
            <label className="flex items-center gap-3 px-3 py-2 rounded-2xl border border-base-200">
              <input
                type="checkbox"
                checked={isExplicit}
                onChange={(e) => setIsExplicit(e.target.checked)}
                className="checkbox checkbox-sm"
              />
              <span className="text-sm text-base-content">Contains explicit content</span>
            </label>
          </div>
        </div>

        <div>
          <label className="text-sm text-base-content/70 block mb-1">Release schedule</label>
          <input
            value={releaseSchedule}
            onChange={(e) => setReleaseSchedule(e.target.value)}
            placeholder="e.g. Weekly, monthly"
            className="input input-bordered w-full"
          />
        </div>

        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={handleCreateStep2Skip}
            className="btn btn-outline btn-primary btn-sm"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={handleCreateStep2Continue}
            disabled={savingSeries}
            className="btn btn-primary"
          >
            {savingSeries ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    );

    const renderStep3 = () => (
      <div className="rounded-2xl border border-base-200 bg-base-100 p-6 shadow-md space-y-4">
        <div className="max-w-xl mx-auto space-y-3">
          <div className="w-full max-w-md mx-auto aspect-square md:aspect-[4/3] rounded-2xl overflow-hidden bg-base-200 border border-dashed border-base-200 flex items-center justify-center shadow-sm">
            {seriesCover ? (
              <img src={seriesCover} alt="Series cover" className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center text-base-content/50 gap-2">
                <ImagePlus className="w-6 h-6" />
                <p className="text-xs">Add a cover image</p>
              </div>
            )}
          </div>
          <label className="btn btn-outline btn-primary btn-sm mx-auto">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={seriesCoverInputRef}
              onChange={(e) => handleSeriesCoverSelection(e.target.files?.[0] ?? null)}
            />
            <UploadCloud className="w-4 h-4" />
            Upload cover
          </label>
          {seriesForm.coverImageUrl && !seriesForm.coverFile && (
            <p className="text-xs text-base-content/60 break-all text-center">Current cover saved.</p>
          )}
          {seriesForm.coverFile && <p className="text-xs text-base-content/80 text-center">{seriesForm.coverFile.name}</p>}
          <div className="flex justify-end">
          <button
            type="button"
            onClick={handleCreateStep3}
            disabled={savingSeries}
            className="btn btn-primary"
          >
            {savingSeries ? 'Saving...' : 'Continue'}
          </button>
          </div>
        </div>
      </div>
    );

    const renderStep4 = () => (
      <div className="rounded-2xl border border-base-200 bg-base-100 p-6 shadow-md space-y-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-1/3 rounded-2xl overflow-hidden border border-base-200 bg-base-200">
            {seriesCover ? (
              <img src={seriesCover} alt="Series cover" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full aspect-[4/3] flex items-center justify-center text-base-content/50">
                <ImagePlus className="w-8 h-8" />
              </div>
            )}
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-xs uppercase text-base-content/60">Series</p>
              <h3 className="text-2xl font-bold">{seriesForm.title || 'Untitled series'}</h3>
              <p className="text-sm text-base-content/70 mt-1">{seriesForm.description || 'No description yet.'}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="rounded-xl border border-base-200 p-3 bg-base-200">
                <p className="text-xs uppercase text-base-content/60">Language</p>
                <p className="font-semibold">{seriesForm.language || 'Not set'}</p>
              </div>
              <div className="rounded-xl border border-base-200 p-3 bg-base-200">
                <p className="text-xs uppercase text-base-content/60">Primary genre</p>
                <p className="font-semibold">{primaryGenreName || 'Not set'}</p>
              </div>
              <div className="rounded-xl border border-base-200 p-3 bg-base-200">
                <p className="text-xs uppercase text-base-content/60">Secondary genres</p>
                <p className="font-semibold">{secondaryNames || 'None'}</p>
              </div>
              <div className="rounded-xl border border-base-200 p-3 bg-base-200">
                <p className="text-xs uppercase text-base-content/60">Audience rating</p>
                <p className="font-semibold">{audienceRating || 'Everyone'}</p>
              </div>
              <div className="rounded-xl border border-base-200 p-3 bg-base-200">
                <p className="text-xs uppercase text-base-content/60">Explicit</p>
                <p className="font-semibold">{isExplicit ? 'Contains explicit content' : 'No explicit content'}</p>
              </div>
              <div className="rounded-xl border border-base-200 p-3 bg-base-200">
                <p className="text-xs uppercase text-base-content/60">Release schedule</p>
                <p className="font-semibold">{releaseSchedule || 'Not provided'}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={handleFinishToEpisodes}
            className="btn btn-primary"
          >
            Add first episode
          </button>
          <button
            type="button"
            onClick={handleFinishToDashboard}
            className="btn btn-outline btn-primary"
          >
            Go to series dashboard
          </button>
        </div>
      </div>
    );

    return (
      <div className="space-y-6">
        {renderCreationStepper()}
        {renderAlerts()}
        {creationStep === 1 && renderStep1()}
        {creationStep === 2 && renderStep2()}
        {creationStep === 3 && renderStep3()}
        {creationStep === 4 && renderStep4()}
      </div>
    );
  };

  const renderDetailView = () => {
    if (creationStep) {
      return (
        <div className="page-shell space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToList}
                className="btn btn-ghost btn-primary btn-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to list
              </button>
              <div>
                <p className="text-xs uppercase text-base-content/60">My series</p>
                <h1 className="text-3xl font-bold">Create series</h1>
              </div>
            </div>
            <button
              onClick={handleStartNewSeries}
              className="btn btn-outline btn-primary btn-sm"
            >
              <PlusCircle className="w-5 h-5" />
              Start over
            </button>
          </div>
          {renderCreationWizard()}
        </div>
      );
    }

    return (
      <div className="page-shell space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToList}
              className="btn btn-ghost btn-primary btn-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to list
            </button>
            <div>
              <p className="text-xs uppercase text-base-content/60">My series</p>
              <h1 className="text-3xl font-bold">{seriesForm.id ? 'Edit series' : 'Create series'}</h1>
            </div>
          </div>
          <button
            onClick={handleStartNewSeries}
            className="btn btn-primary btn-sm"
          >
            <PlusCircle className="w-5 h-5" />
            New series
          </button>
        </div>

        {renderAlerts()}

        <form onSubmit={handleSeriesSave} className="grid lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="rounded-md border border-base-200 p-4 space-y-3 shadow-md">
              <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden bg-base-200 border border-dashed border-base-200 flex items-center justify-center shadow-sm">
                {seriesCover ? (
                  <img src={seriesCover} alt="Series cover" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center text-base-content/50 gap-2">
                    <ImagePlus className="w-6 h-6" />
                    <p className="text-xs">Add a cover image</p>
                  </div>
                )}
              </div>
              <label className="btn btn-outline btn-primary btn-sm">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={seriesCoverInputRef}
                  onChange={(e) => handleSeriesCoverSelection(e.target.files?.[0] ?? null)}
                />
                <UploadCloud className="w-4 h-4" />
                Upload cover
              </label>
              {seriesForm.coverImageUrl && !seriesForm.coverFile && (
                <p className="text-xs text-base-content/60 break-all">Current cover saved.</p>
              )}
              {seriesForm.coverFile && <p className="text-xs text-base-content/80">{seriesForm.coverFile.name}</p>}
            </div>

            <div className="rounded-md border border-base-200 p-4 space-y-3 shadow-md">
              <div>
                <label className="text-sm text-base-content/70 block mb-1">Slug</label>
                <input
                  value={seriesForm.slug}
                  onChange={(e) => {
                    setSeriesForm((prev) => ({ ...prev, slug: slugify(e.target.value) }));
                    setSeriesSlugEdited(true);
                  }}
                  className="input input-bordered w-full"
                  placeholder="auto-generated"
                />
                <p className="text-xs text-base-content/60 mt-1">Auto-generated and kept unique.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-base-content/70 block mb-1">Language</label>
                  <select
                    value={seriesForm.language}
                    onChange={(e) => setSeriesForm((prev) => ({ ...prev, language: e.target.value }))}
                    className="select select-bordered w-full"
                  >
                    {LANG_OPTIONS.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-base-content/70 block mb-1">Status</label>
                  <select
                    value={seriesForm.status}
                    onChange={(e) => setSeriesForm((prev) => ({ ...prev, status: e.target.value }))}
                    className="select select-bordered w-full"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-md border border-base-200 p-5 space-y-4 shadow-md">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-base-content/70 block mb-1">Title</label>
                  <input
                    required
                    value={seriesForm.title}
                    onChange={(e) => {
                      const nextTitle = e.target.value;
                      setSeriesForm((prev) => ({
                        ...prev,
                        title: nextTitle,
                        slug: seriesSlugEdited ? prev.slug : slugify(nextTitle),
                      }));
                    }}
                    className="input input-bordered w-full"
                    placeholder="e.g. Science Weekly"
                  />
                </div>
                <div>
                  <label className="text-sm text-base-content/70 block mb-1">Status preview</label>
                  <div className="h-12 px-4 flex items-center rounded-2xl border border-base-200 bg-base-200 text-sm text-base-content/70">
                    {seriesForm.id ? 'Editing existing series' : 'Drafting a new series'}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm text-base-content/70 block mb-1">Description</label>
                <textarea
                  value={seriesForm.description}
                  onChange={(e) => setSeriesForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="textarea textarea-bordered w-full"
                  placeholder="What is this series about?"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-base-content/70">Genres</label>
                  <span className="text-xs text-base-content/50">{seriesForm.genreIds.length} selected</span>
                </div>
                <div className="rounded-2xl border border-base-200 p-3 max-h-48 overflow-auto grid grid-cols-2 gap-2 shadow-sm">
                  {genres.map((genre) => (
                    <label key={genre.id} className="flex items-center gap-2 text-sm text-base-content/80">
                      <input
                        type="checkbox"
                        checked={seriesForm.genreIds.includes(genre.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSeriesForm((prev) => ({
                            ...prev,
                            genreIds: checked
                              ? [...prev.genreIds, genre.id]
                              : prev.genreIds.filter((id) => id !== genre.id),
                          }));
                        }}
                        className="checkbox checkbox-sm"
                      />
                      <span>{genre.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-3">
                {seriesForm.id && (
                  <button
                    type="button"
                    onClick={() => {
                      resetSeriesForm();
                      setSelectedSeriesId(null);
                    }}
                    className="btn btn-outline btn-primary btn-sm"
                  >
                    Start new
                  </button>
                )}
                <button
                  type="submit"
                  disabled={savingSeries}
                  className="btn btn-primary"
                >
                  {savingSeries ? 'Saving...' : seriesForm.id ? 'Update series' : 'Create series'}
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="rounded-md border border-base-200 p-5 space-y-4 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Episodes</h2>
              {selectedSeries && <p className="text-sm text-base-content/60">Series: {selectedSeries.title}</p>}
            </div>
            <button
              type="button"
              onClick={() => resetEpisodeForm()}
              className="btn btn-outline btn-primary btn-sm"
            >
              Add new episode
            </button>
          </div>
          {!seriesForm.id && <p className="text-base-content/60 text-sm">Save the series first to add episodes.</p>}
          {seriesForm.id && (
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="lg:col-span-1 space-y-3">
                {(selectedSeries?.episodes ?? []).length === 0 && (
                  <p className="text-base-content/60 text-sm">No episodes yet. Add your first one.</p>
                )}
                {(selectedSeries?.episodes ?? []).map((ep) => (
                  <div key={ep.id} className="p-4 rounded-2xl border border-base-200 bg-base-200 space-y-2 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-base-100 border border-base-200 flex items-center justify-center text-xs font-semibold text-base-content/80">
                          #{ep.episode_number || (selectedSeries?.episodes?.indexOf(ep) ?? 0) + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{ep.title}</p>
                          <p className="text-xs text-base-content/60">
                            {ep.status ?? 'draft'} {ep.is_preview ? '· preview' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEpisodeEdit(ep)}
                          className="btn btn-ghost btn-primary btn-square btn-sm"
                          aria-label="Edit episode"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEpisodeDelete(ep.id)}
                          className="btn btn-ghost btn-error btn-square btn-sm"
                          aria-label="Delete episode"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-base-content/60 line-clamp-2">{ep.description}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={handleEpisodeSave} className="lg:col-span-2 space-y-3 rounded-2xl border border-base-200 p-4 shadow-md">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-base-content/70 block mb-1">Title</label>
                    <input
                      required
                      value={episodeForm.title}
                      onChange={(e) => {
                        const nextTitle = e.target.value;
                        setEpisodeForm((prev) => ({
                          ...prev,
                          title: nextTitle,
                          slug: episodeSlugEdited ? prev.slug : slugify(nextTitle),
                        }));
                      }}
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-base-content/70 block mb-1">Slug</label>
                    <input
                      value={episodeForm.slug}
                      onChange={(e) => {
                        setEpisodeForm((prev) => ({ ...prev, slug: slugify(e.target.value) }));
                        setEpisodeSlugEdited(true);
                      }}
                      className="input input-bordered w-full"
                      placeholder="auto-generated"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-base-content/70 block mb-1">Episode number</label>
                    <input
                      type="number"
                      min={1}
                      value={episodeForm.episodeNumber}
                      onChange={(e) =>
                        setEpisodeForm((prev) => ({ ...prev, episodeNumber: Number(e.target.value) || 1 }))
                      }
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-base-content/70 block mb-1">Status</label>
                    <select
                      value={episodeForm.status}
                      onChange={(e) => setEpisodeForm((prev) => ({ ...prev, status: e.target.value }))}
                      className="select select-bordered w-full"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input
                      id="is_preview"
                      type="checkbox"
                      checked={episodeForm.isPreview}
                      onChange={(e) => setEpisodeForm((prev) => ({ ...prev, isPreview: e.target.checked }))}
                      className="checkbox checkbox-sm"
                    />
                    <label htmlFor="is_preview" className="text-sm text-base-content/80">
                      Preview episode
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-base-content/70 block">Audio file</label>
                  <label className="flex items-center justify-between gap-3 px-3 py-3 rounded-2xl border border-dashed border-base-200 hover:border-primary cursor-pointer shadow-sm">
                    <div className="flex items-center gap-3 text-sm">
                      <FileAudio className="w-5 h-5 text-base-content/60" />
                      <div className="flex flex-col">
                        <span className="font-medium text-base-content">Upload or replace audio</span>
                        <span className="text-xs text-base-content/60">mp3, wav, m4a supported</span>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        setEpisodeForm((prev) => ({ ...prev, audioFile: file || null }));
                      }}
                    />
                    <UploadCloud className="w-4 h-4 text-base-content/60" />
                  </label>
                  {episodeForm.audioFile && <p className="text-xs text-base-content/80">Queued: {episodeForm.audioFile.name}</p>}
                  {!episodeForm.audioFile && episodeForm.audioUrl && (
                    <p className="text-xs text-base-content/60 break-all">Current file: {episodeForm.audioUrl}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-base-content/70 block mb-1">Description</label>
                  <textarea
                    value={episodeForm.description}
                    onChange={(e) => setEpisodeForm((prev) => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="textarea textarea-bordered w-full"
                  />
                </div>
                <div className="flex flex-wrap justify-end gap-3">
                  {episodeForm.id && (
                    <button
                      type="button"
                      onClick={() => resetEpisodeForm()}
                      className="btn btn-outline btn-primary btn-sm"
                    >
                      Cancel edit
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={savingEpisode}
                    className="btn btn-primary"
                  >
                    {savingEpisode ? 'Saving...' : episodeForm.id ? 'Update episode' : 'Create episode'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  };

  return view === 'list' ? renderListView() : renderDetailView();
}
